import React from "react";

import { faEllipsisH } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	AvatarProfile,
	Chip,
	DropDownMenu,
	type ICellRendererParams,
	Icon,
	Link,
	ModalConfirmation,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
	TextDate,
	toast,
} from "@performyard/py-components";
import type { ActionItemWithMeetingInfoJson } from "@performyard/service-contracts";

import { LinkifiedText } from "../../../../components/LinkifiedText";
import EditActionItemModal from "./EditActionItemModal";

import { useEditMeetingActionItem } from "../../../../api/hooks";

import { MEETING_BREADCRUMB_SOURCE, appendMeetingBreadcrumbSource } from "../../utils/breadcrumbSource";

interface ActionItemsTableProps {
	actionItems: ActionItemWithMeetingInfoJson[];
	isFetchingActionItems: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
}

const ActionItemsTable = (props: ActionItemsTableProps) => {
	const { actionItems, isFetchingActionItems, activeSort, activeSortDirection, handleColumnHeaderClick, userId } =
		props;

	const { mutate: editActionItem, isPending: isUpdating } = useEditMeetingActionItem();

	const [editingActionItem, setEditingActionItem] = React.useState<ActionItemWithMeetingInfoJson | null>(null);
	const [deletingActionItemId, setDeletingActionItemId] = React.useState<string | null>(null);

	const getAssignee = (actionItem: ActionItemWithMeetingInfoJson) => {
		return actionItem.participants.find((p) => p.role === "assignee");
	};

	const isCreator = (actionItem: ActionItemWithMeetingInfoJson) => {
		return actionItem.created.userId === userId;
	};

	const handleStatusToggle = (actionItem: ActionItemWithMeetingInfoJson) => {
		const newStatus: "complete" | "incomplete" =
			actionItem.mostRecentMeetingStatus === "complete" ? "incomplete" : "complete";
		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { meetingId: m.meetingId, status: newStatus } : m
		);

		editActionItem(
			{
				actionItemId: actionItem._id,
				data: {
					meetings: updatedMeetings,
				},
			},
			{
				onSuccess: () => {
					const message =
						newStatus === "complete" ? "Action Item Marked Complete" : "Action Item Marked Incomplete";
					toast.success(message);
				},
				onError: () => {
					toast.error("Failed to update action item");
				},
			}
		);
	};

	const handleDelete = (actionItemId: string) => {
		const actionItem = actionItems.find((item) => item._id === actionItemId);
		if (!actionItem) return;

		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { ...m, status: "deleted" as const } : m
		);

		editActionItem(
			{
				actionItemId,
				data: {
					meetings: updatedMeetings,
				},
			},
			{
				onSuccess: () => {
					setDeletingActionItemId(null);
					toast.success("Action Item Updated");
				},
				onError: () => {
					toast.error("Failed to delete action item");
				},
			}
		);
	};

	const columnDefs: PYColumnDef<ActionItemWithMeetingInfoJson>[] = [
		{
			field: "name",
			headerName: "Action Item Name",
			sortable: true,
			resizable: false,
			width: 308,
			minWidth: 280,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<Text
							size="paragraph-3"
							weight="regular"
							dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
						>
							<LinkifiedText text={actionItem.name} />
						</Text>
					</div>
				);
			},
		},
		{
			field: "assignee",
			headerName: "Assignee",
			sortable: true,
			resizable: false,
			width: 160,
			minWidth: 160,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				const assignee = getAssignee(actionItem);
				const assigneeProfile = assignee ? (
					<AvatarProfile employeeId={assignee.userId} employeeName={assignee.name || ""} />
				) : (
					<AvatarProfile employeeId="" employeeName="Unassigned" noLink />
				);
				return <div className="flex h-full w-full min-w-0 items-center">{assigneeProfile}</div>;
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: true,
			resizable: false,
			width: 116,
			minWidth: 116,
			maxWidth: 116,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				const statusChip =
					actionItem.mostRecentMeetingStatus === "complete" ? (
						<Chip label="Complete" color="green" size={24} />
					) : (
						<Chip label="Incomplete" color="neutral" size={24} />
					);
				return <div className="flex h-full w-full min-w-0 items-center">{statusChip}</div>;
			},
		},
		{
			field: "dueDate",
			headerName: "Due Date",
			sortable: true,
			resizable: false,
			width: 112,
			minWidth: 112,
			maxWidth: 112,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem?.dueDate) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<TextDate date={actionItem.dueDate} hasTime={false} dateSize="medium" />
					</div>
				);
			},
		},
		{
			field: "createdBy",
			headerName: "Created By",
			sortable: true,
			resizable: false,
			width: 160,
			minWidth: 160,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				const creatorProfile =
					actionItem.created.userId && actionItem.created.name ? (
						<AvatarProfile employeeId={actionItem.created.userId} employeeName={actionItem.created.name} />
					) : (
						<AvatarProfile employeeId="" employeeName="Unknown" noLink />
					);
				return <div className="flex h-full w-full min-w-0 items-center">{creatorProfile}</div>;
			},
		},
		{
			field: "workspaceName",
			headerName: "Meeting Workspace Name",
			sortable: true,
			resizable: false,
			width: 220,
			minWidth: 220,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<Link
							href={appendMeetingBreadcrumbSource(
								`/meeting-workspaces/${actionItem.workspaceId}`,
								MEETING_BREADCRUMB_SOURCE.actionItems
							)}
						>
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
							>
								{actionItem.workspaceName}
							</Text>
						</Link>
					</div>
				);
			},
		},
		{
			field: "control",
			headerName: "",
			sortable: false,
			resizable: false,
			width: 56,
			minWidth: 56,
			maxWidth: 56,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ActionItemWithMeetingInfoJson>) => {
				const actionItem = params.data;
				if (!actionItem) return null;
				const creator = isCreator(actionItem);
				const assignee = getAssignee(actionItem);
				const isAssignee = assignee?.userId === userId;

				// Build options based on permissions
				const options: Array<{ key: string; label: string; onClick: () => void }> = [];

				// Everyone who can see the item can mark complete/incomplete
				if (creator || isAssignee) {
					const statusLabel =
						actionItem.mostRecentMeetingStatus === "complete" ? "Mark Incomplete" : "Mark Complete";
					options.push({
						key: "toggle-status",
						label: statusLabel,
						onClick: () => handleStatusToggle(actionItem),
					});
				}

				// Only creator can edit and delete
				if (creator) {
					options.push({
						key: "edit",
						label: "Edit",
						onClick: () => setEditingActionItem(actionItem),
					});
					options.push({
						key: "delete",
						label: "Delete",
						onClick: () => setDeletingActionItemId(actionItem._id),
					});
				}

				if (options.length === 0) {
					return <div className="h-8" />;
				}

				return (
					<div className="flex h-full w-full min-w-0 items-center justify-center">
						<DropDownMenu
							options={options}
							placement="bottom-end"
							className="group flex h-full items-center"
						>
							<Icon
								icon={faEllipsisH}
								size={24}
								color="neutral-7"
								dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
							/>
						</DropDownMenu>
					</div>
				);
			},
		},
	];

	const onSortChanged = (event: SortChangedEvent<ActionItemWithMeetingInfoJson>) => {
		const sortedColumn = event.api.getColumnState().find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<>
			<div data-testid="actionItemsContainer">
				<Table<ActionItemWithMeetingInfoJson>
					autoHeight
					columnSizing="fit-grid"
					rowData={actionItems}
					columnDefs={columnDefs}
					loading={isFetchingActionItems || isUpdating}
					getRowId={(params) => params.data._id}
					onSortChanged={onSortChanged}
					emptyStateText="No Action Items Found"
					initialState={{
						sort: {
							sortModel: activeSort
								? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
								: [],
						},
					}}
					animateRows={false}
				/>
			</div>

			<EditActionItemModal
				isOpen={editingActionItem !== null}
				handleClose={() => setEditingActionItem(null)}
				actionItem={editingActionItem}
			/>

			<ModalConfirmation
				isOpen={deletingActionItemId !== null}
				onClose={() => setDeletingActionItemId(null)}
				title="Delete Action Item?"
				subtitle="Are you sure you want to delete this action item? This action is permanent and cannot be undone."
				negativeButtonProps={{
					children: "Cancel",
					onClick: () => setDeletingActionItemId(null),
				}}
				positiveButtonProps={{
					children: "Yes, Delete Action Item",
					onClick: () => {
						if (deletingActionItemId) {
							handleDelete(deletingActionItemId);
						}
					},
					disabled: isUpdating,
					color: "red",
				}}
				dangerouslySetClassName="max-w-[416px]"
			/>
		</>
	);
};

export default ActionItemsTable;
